import React, { useState, useEffect } from 'react';
import {
    DndContext,
    DragOverlay,
    closestCorners,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { X, Eye, List, Plus, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import DayCard from './DayCard';
import TimelineItem from './TimelineItem';
import AddItemMenu from './AddItemMenu';
import ItemSelectorModal from './ItemSelectorModal';

const TimelineEditor = ({ isOpen, onClose, course, onSuccess }) => {
    // View modes
    const [viewMode, setViewMode] = useState('detailed'); // 'detailed' or 'compact'

    // Day management
    const [days, setDays] = useState([]);
    const [expandedDays, setExpandedDays] = useState(new Set([0])); // Day 0 expanded by default

    // Item management
    const [items, setItems] = useState({});
    const [activeId, setActiveId] = useState(null);
    const [activeItem, setActiveItem] = useState(null);

    // Modals
    const [isAddMenuOpen, setIsAddMenuOpen] = useState(null); // Day number or null
    const [isSelectorOpen, setIsSelectorOpen] = useState(false);
    const [selectedItemType, setSelectedItemType] = useState(null);
    const [addingToDay, setAddingToDay] = useState(null);

    // Add Day popover
    const [isAddDayOpen, setIsAddDayOpen] = useState(false);
    const [newDayInput, setNewDayInput] = useState('');

    // Loading states
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Sensors for drag and drop
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    useEffect(() => {
        if (isOpen && course) {
            fetchTimelineData();
        }
    }, [isOpen, course]);

    const fetchTimelineData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await api.get(`/courses/${course.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            const courseData = response.data.course;
            const courseItems = courseData.items || [];

            // Generate days based on course duration
            const durationDays = courseData.duration_days || 30;
            // Use specific days that make sense for onboarding: 0, 1, 3, 7, 14, 21, 30, etc.
            const defaultDays = [0, 1, 3, 7, 14, 21, 30].filter(d => d <= durationDays);

            // Add any days that have items but aren't in default list
            const daysWithItems = [...new Set(courseItems.map(item => item.relative_day))];
            const allDays = [...new Set([...defaultDays, ...daysWithItems])].sort((a, b) => a - b);

            setDays(allDays);

            // Group items by day
            const itemsByDay = {};
            allDays.forEach(day => {
                itemsByDay[day] = courseItems
                    .filter(item => item.relative_day === day)
                    .sort((a, b) => a.order_index - b.order_index);
            });
            setItems(itemsByDay);

        } catch (error) {
            console.error('Error fetching timeline data:', error);
            toast.error('Failed to load timeline');
        } finally {
            setLoading(false);
        }
    };

    const handleDragStart = (event) => {
        const { active } = event;
        setActiveId(active.id);

        // Find the active item across all days
        for (const day in items) {
            const item = items[day].find(i => i.id === active.id);
            if (item) {
                setActiveItem(item);
                break;
            }
        }
    };

    const handleDragOver = (event) => {
        const { active, over } = event;
        if (!over) return;

        const activeContainer = findContainer(active.id);
        const overContainer = findContainer(over.id) || over.id;

        if (!activeContainer || !overContainer || activeContainer === overContainer) {
            return;
        }

        setItems((prev) => {
            const activeItems = [...prev[activeContainer]];
            const overItems = [...prev[overContainer]] || [];
            const activeIndex = activeItems.findIndex(i => i.id === active.id);
            const activeItem = activeItems[activeIndex];

            // Remove from old container
            activeItems.splice(activeIndex, 1);

            // Add to new container
            const overIndex = overItems.findIndex(i => i.id === over.id);
            const newIndex = overIndex >= 0 ? overIndex : overItems.length;
            overItems.splice(newIndex, 0, { ...activeItem, relative_day: parseInt(overContainer) });

            return {
                ...prev,
                [activeContainer]: activeItems,
                [overContainer]: overItems,
            };
        });
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        setActiveId(null);
        setActiveItem(null);

        if (!over) return;

        const activeContainer = findContainer(active.id);
        const overContainer = findContainer(over.id) || over.id;

        if (!activeContainer) return;

        if (activeContainer === overContainer) {
            // Reorder within same day
            setItems((prev) => {
                const dayItems = [...prev[activeContainer]];
                const oldIndex = dayItems.findIndex(i => i.id === active.id);
                const newIndex = dayItems.findIndex(i => i.id === over.id);

                if (oldIndex !== newIndex && newIndex !== -1) {
                    const [movedItem] = dayItems.splice(oldIndex, 1);
                    dayItems.splice(newIndex, 0, movedItem);
                }

                return { ...prev, [activeContainer]: dayItems };
            });
        }
    };

    const findContainer = (id) => {
        if (days.includes(parseInt(id))) {
            return id;
        }
        for (const day in items) {
            if (items[day].find(item => item.id === id)) {
                return day;
            }
        }
        return null;
    };

    const toggleDayExpanded = (day) => {
        setExpandedDays(prev => {
            const newSet = new Set(prev);
            if (newSet.has(day)) {
                newSet.delete(day);
            } else {
                newSet.add(day);
            }
            return newSet;
        });
    };

    const handleAddItem = (day, type) => {
        setAddingToDay(day);
        setSelectedItemType(type);
        setIsAddMenuOpen(null);
        setIsSelectorOpen(true);
    };

    const handleItemSelected = async (selectedItem) => {
        if (!addingToDay === null || !selectedItemType || !selectedItem) return;

        const loadingToast = toast.loading('Adding item to timeline...');
        try {
            const token = localStorage.getItem('token');
            const response = await api.post(`/courses/${course.id}/items`, {
                item_type: selectedItemType,
                item_id: selectedItem.id,
                relative_day: addingToDay,
                order_index: (items[addingToDay]?.length || 0)
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // Add to local state
            const newItem = {
                ...response.data.item,
                item_title: selectedItem.title,
                task_type: selectedItem.task_type
            };

            setItems(prev => ({
                ...prev,
                [addingToDay]: [...(prev[addingToDay] || []), newItem]
            }));

            toast.success('Item added to timeline', { id: loadingToast });
        } catch (error) {
            console.error('Error adding item:', error);
            toast.error('Failed to add item', { id: loadingToast });
        }

        setIsSelectorOpen(false);
        setSelectedItemType(null);
        setAddingToDay(null);
    };

    const handleRemoveItem = async (itemId, day) => {
        const loadingToast = toast.loading('Removing item...');
        try {
            const token = localStorage.getItem('token');
            await api.delete(`/courses/${course.id}/items/${itemId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setItems(prev => ({
                ...prev,
                [day]: prev[day].filter(i => i.id !== itemId)
            }));

            toast.success('Item removed', { id: loadingToast });
        } catch (error) {
            console.error('Error removing item:', error);
            toast.error('Failed to remove item', { id: loadingToast });
        }
    };

    const handleTimeChange = (itemId, newTime) => {
        setItems(prev => {
            const newItems = { ...prev };
            // Find item and update time
            for (const day in newItems) {
                const itemIndex = newItems[day].findIndex(i => i.id === itemId);
                if (itemIndex !== -1) {
                    const updatedItem = { ...newItems[day][itemIndex], release_time: newTime };
                    const updatedDayItems = [...newItems[day]];
                    updatedDayItems[itemIndex] = updatedItem;
                    newItems[day] = updatedDayItems;
                    break;
                }
            }
            return newItems;
        });
    };

    const handleSaveOrder = async () => {
        setSaving(true);
        const loadingToast = toast.loading('Saving timeline order...');

        try {
            const token = localStorage.getItem('token');

            // Update all items with their new relative_day and order_index
            const updates = [];
            for (const day in items) {
                items[day].forEach((item, index) => {
                    updates.push({
                        id: item.id,
                        relative_day: parseInt(day),
                        order_index: index,
                        release_time: item.release_time || null
                    });
                });
            }

            // Batch update (you may need to create this endpoint)
            await api.put(`/courses/${course.id}/items/reorder`, { items: updates }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            toast.success('Timeline saved', { id: loadingToast });
            onSuccess?.();
        } catch (error) {
            console.error('Error saving timeline:', error);
            toast.error('Failed to save timeline', { id: loadingToast });
        } finally {
            setSaving(false);
        }
    };

    const handleAddDay = () => {
        const dayNum = parseInt(newDayInput, 10);
        const maxDuration = course?.duration_days || 30;

        // Validation
        if (isNaN(dayNum)) {
            toast.error('Please enter a valid day number');
            return;
        }

        if (dayNum < 0 || dayNum > maxDuration) {
            toast.error(`Day must be between 0 and ${maxDuration}`);
            return;
        }

        if (days.includes(dayNum)) {
            toast.error(`Day ${dayNum} already exists in the timeline`);
            return;
        }

        // Add the day
        setDays(prev => [...prev, dayNum].sort((a, b) => a - b));
        setItems(prev => ({ ...prev, [dayNum]: [] }));
        setExpandedDays(prev => new Set(prev).add(dayNum));
        setNewDayInput('');
        setIsAddDayOpen(false);
        toast.success(`Day ${dayNum} added to timeline`);
    };



    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white w-full h-full flex flex-col">
                {/* Header */}
                <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between bg-white flex-shrink-0">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                        <div>
                            <h2 className="text-xl font-bold text-gray-800">Timeline Editor</h2>
                            <p className="text-sm text-gray-600">{course?.name}</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">

                        {/* View Mode Toggle */}
                        <div className="flex items-center bg-gray-100 rounded-lg p-1">
                            <button
                                onClick={() => setViewMode('detailed')}
                                className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'detailed'
                                    ? 'bg-white text-gray-800 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-800'
                                    }`}
                            >
                                <Eye className="w-4 h-4" />
                                Detailed
                            </button>
                            <button
                                onClick={() => setViewMode('compact')}
                                className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'compact'
                                    ? 'bg-white text-gray-800 shadow-sm'
                                    : 'text-gray-600 hover:text-gray-800'
                                    }`}
                            >
                                <List className="w-4 h-4" />
                                Compact
                            </button>
                        </div>

                        <button
                            onClick={handleSaveOrder}
                            disabled={saving}
                            className="flex items-center gap-2 bg-primary hover:bg-primary-hover text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
                        >
                            <Save className="w-4 h-4" />
                            Save
                        </button>
                    </div>
                </div>

                {/* Split View Content */}
                <div className="flex-1 flex overflow-hidden">


                    {/* Timeline Content */}
                    <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
                        {loading ? (
                            <div className="flex items-center justify-center h-64">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                            </div>
                        ) : (
                            <div className="max-w-4xl mx-auto">
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCorners}
                                    onDragStart={handleDragStart}
                                    onDragOver={handleDragOver}
                                    onDragEnd={handleDragEnd}
                                >
                                    <div className="space-y-4">
                                        {days.map((day) => (
                                            <DayCard
                                                key={day}
                                                day={day}
                                                items={items[day] || []}
                                                isExpanded={expandedDays.has(day)}
                                                onToggle={() => toggleDayExpanded(day)}
                                                viewMode={viewMode}
                                                onAddItem={(type) => handleAddItem(day, type)}
                                                onRemoveItem={(itemId) => handleRemoveItem(itemId, day)}
                                                onTimeChange={handleTimeChange}
                                                isAddMenuOpen={isAddMenuOpen === day}
                                                setIsAddMenuOpen={(open) => setIsAddMenuOpen(open ? day : null)}
                                            />
                                        ))}
                                    </div>

                                    {/* Drag Overlay */}
                                    <DragOverlay>
                                        {activeItem ? (
                                            <TimelineItem
                                                item={activeItem}
                                                viewMode={viewMode}
                                                isDragging
                                            />
                                        ) : null}
                                    </DragOverlay>
                                </DndContext>

                                {/* Add Day Section */}
                                <div className="mt-4 relative">
                                    {!isAddDayOpen ? (
                                        <button
                                            onClick={() => setIsAddDayOpen(true)}
                                            className="w-full py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-2"
                                        >
                                            <Plus className="w-4 h-4" />
                                            Add Day
                                        </button>
                                    ) : (
                                        <div className="bg-white border-2 border-primary rounded-xl p-4 shadow-lg">
                                            <div className="flex items-center gap-3">
                                                <div className="flex-1">
                                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                                        Day Number (0 - {course?.duration_days || 30})
                                                    </label>
                                                    <input
                                                        type="number"
                                                        value={newDayInput}
                                                        onChange={(e) => setNewDayInput(e.target.value)}
                                                        min="0"
                                                        max={course?.duration_days || 30}
                                                        placeholder="e.g., 2, 5, 10"
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                                                        autoFocus
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') handleAddDay();
                                                            if (e.key === 'Escape') {
                                                                setIsAddDayOpen(false);
                                                                setNewDayInput('');
                                                            }
                                                        }}
                                                    />
                                                </div>
                                                <button
                                                    onClick={handleAddDay}
                                                    className="px-4 py-2 bg-primary hover:bg-primary-hover text-white rounded-lg font-medium text-sm transition-colors"
                                                >
                                                    Add
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setIsAddDayOpen(false);
                                                        setNewDayInput('');
                                                    }}
                                                    className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                            <p className="text-xs text-gray-500 mt-2">
                                                Existing days: {days.join(', ')}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Item Selector Modal */}
            <ItemSelectorModal
                isOpen={isSelectorOpen}
                onClose={() => {
                    setIsSelectorOpen(false);
                    setSelectedItemType(null);
                    setAddingToDay(null);
                }}
                onSelect={handleItemSelected}
                itemType={selectedItemType}
                courseType={course?.course_type}
            />
        </div>
    );
};

export default TimelineEditor;
